import clsx from "clsx";
import Icon from "@/components/icon";
import { useState, useRef, useCallback, useMemo, useEffect, memo } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";

const SPEED_OPTIONS_MOBILE = [0.5, 1, 1.2, 1.5, 1.7, 2];

interface SettingsMenuProps {
  currentSpeed: number;
  currentQuality: string;
  qualities: string[];
  onSpeedChange: (speed: number) => void;
  onQualityChange: (quality: string) => void;
}

export const SettingsMenu = memo(({
  currentSpeed,
  currentQuality,
  qualities,
  onSpeedChange,
  onQualityChange,
}: SettingsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const [localSpeed, setLocalSpeed] = useState(currentSpeed);

  const commitSpeed = useCallback(
    (speed: number) => {
      setLocalSpeed(speed);
      onSpeedChange(speed);
    },
    [onSpeedChange]
  );

  useEffect(() => {
    setLocalSpeed(currentSpeed);
  }, [currentSpeed]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const target = event.target as Node;
        const isSettingsButton = buttonRef.current?.contains(target);
        const isDrawerContent = drawerRef.current?.contains(target);

        if (!isSettingsButton && !isDrawerContent) {
          setIsOpen(false);
          setActiveSubmenu(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const formatSpeed = useCallback((speed: number): string => {
    return `${speed}`;
  }, []);

  const formatQuality = useCallback((quality: string): string => {
    if (quality === "auto") return "خودکار";
    return quality;
  }, []);

  const handleSubmenuClick = useCallback(
    (submenu: string) => {
      setActiveSubmenu(activeSubmenu === submenu ? null : submenu);
    },
    [activeSubmenu]
  );

  const handleBackClick = useCallback(() => {
    setActiveSubmenu(null);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setActiveSubmenu(null);
    }
  }, [isOpen]);

  const currentSpeedIndex = useMemo(() => {
    return SPEED_OPTIONS_MOBILE.indexOf(localSpeed);
  }, [localSpeed]);

  const handleSpeedIncrease = useCallback(() => {
    if (currentSpeedIndex < SPEED_OPTIONS_MOBILE.length - 1) {
      commitSpeed(SPEED_OPTIONS_MOBILE[currentSpeedIndex + 1]);
    }
  }, [currentSpeedIndex, commitSpeed]);

  const handleSpeedDecrease = useCallback(() => {
    if (currentSpeedIndex > 0) {
      commitSpeed(SPEED_OPTIONS_MOBILE[currentSpeedIndex - 1]);
    }
  }, [currentSpeedIndex, commitSpeed]);

  const handleSpeedSelect = useCallback(
    (speed: number) => {
      commitSpeed(speed);
    },
    [commitSpeed]
  );

  const handleQualitySelect = useCallback(
    (quality: string) => {
      onQualityChange(quality);
      handleBackClick();
    },
    [onQualityChange, handleBackClick]
  );

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={toggleMenu}
        className={clsx(
          "relative size-8 flex items-center justify-center",
          "text-white hover:text-gray-300",
          "rounded-md hover:bg-[#ffffff1a]"
        )}
      >
        <Icon name="setting" size={20} />
      </button>

      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) setActiveSubmenu(null);
        }}
      >
        <DrawerContent
          ref={drawerRef}
          className="bg-neutral-900 border-t border-neutral-800 text-right p-0 pt-2"
          hideHandle
        >
          <div className="px-4 py-3 border-b border-[#ffffff1a]">
            <div className="text-white font-medium flex items-center gap-2">
              {activeSubmenu && (
                <button
                  type="button"
                  onClick={handleBackClick}
                  className="text-[#ffffffb3] hover:text-white transition-colors p-1 -m-1"
                >
                  <Icon
                    name="chevron-right"
                    size={20}
                    className="text-[#ffffff80]"
                  />
                </button>
              )}
              <span>
                {!activeSubmenu
                  ? "تنظیمات"
                  : activeSubmenu === "speed"
                  ? "سرعت پخش"
                  : "کیفیت"}
              </span>
            </div>
          </div>

          <div className="p-4 text-xs">
            {!activeSubmenu ? (
              <div className="space-y-1">
                <button
                  type="button"
                  className="flex items-center justify-between px-3 py-4 hover:bg-[#ffffff1a] transition-colors cursor-pointer rounded-lg w-full"
                  onClick={() => handleSubmenuClick("speed")}
                >
                  <span className="text-white ">سرعت پخش</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#ffffffb3] ">
                      {formatSpeed(localSpeed)}
                    </span>
                    <Icon
                      name="chevron-left"
                      size={20}
                      className="text-[#ffffff80]"
                    />
                  </div>
                </button>
                <button
                  type="button"
                  className="flex items-center justify-between px-3 py-4 hover:bg-[#ffffff1a] transition-colors cursor-pointer rounded-lg w-full"
                  onClick={() => handleSubmenuClick("quality")}
                >
                  <span className="text-white ">کیفیت</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#ffffffb3] ">
                      {currentQuality === "auto"
                        ? "خودکار"
                        : formatQuality(currentQuality)}
                    </span>
                    <Icon
                      name="chevron-left"
                      size={20}
                      className="text-[#ffffff80]"
                    />
                  </div>
                </button>
              </div>
            ) : (
              <div>
                <div className="py-1">
                  {activeSubmenu === "speed" && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleSpeedIncrease}
                          className="w-8 h-8 rounded-full bg-[#ffffff1a] hover:bg-[#ffffff33] flex items-center justify-center transition-colors"
                          disabled={
                            localSpeed >=
                            SPEED_OPTIONS_MOBILE[
                              SPEED_OPTIONS_MOBILE.length - 1
                            ]
                          }
                        >
                          <Icon name="plus" size={20} className="text-white" />
                        </button>

                        <div className="flex-1 relative" dir="ltr">
                          <input
                            type="range"
                            min={0}
                            max={SPEED_OPTIONS_MOBILE.length - 1}
                            step={1}
                            value={currentSpeedIndex}
                            onChange={(e) =>
                              commitSpeed(
                                SPEED_OPTIONS_MOBILE[Number(e.target.value)]
                              )
                            }
                            className="w-full h-1 bg-[#ffffff4d] rounded-lg appearance-none cursor-pointer"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={handleSpeedDecrease}
                          className="w-8 h-8 rounded-full bg-[#ffffff1a] hover:bg-[#ffffff33] flex items-center justify-center transition-colors"
                          disabled={localSpeed <= SPEED_OPTIONS_MOBILE[0]}
                        >
                          <Icon name="minus" size={20} className="text-white" />
                        </button>
                      </div>

                      <div className="grid grid-cols-6 gap-2" dir="ltr">
                        {SPEED_OPTIONS_MOBILE.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handleSpeedSelect(option)}
                            className={clsx(
                              "py-1 grow  rounded-full text-sm transition-all border border-transparent",
                              "flex items-center justify-center",
                              localSpeed === option
                                ? "bg-white text-neutral-900 font-semibold"
                                : "bg-[#ffffff1a] text-white hover:bg-[#ffffff33]"
                            )}
                          >
                            {formatSpeed(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeSubmenu === "quality" && (
                    <div className="space-y-1">
                      {qualities.map((quality) => (
                        <button
                          key={quality}
                          type="button"
                          onClick={() => handleQualitySelect(quality)}
                          className={clsx(
                            "w-full text-right font-semibold px-3 py-4 rounded-lg transition-colors flex items-center justify-between",
                            currentQuality === quality
                              ? "text-white bg-[#ffffff1a]"
                              : "text-[#ffffffcc] hover:bg-[#ffffff33]"
                          )}
                        >
                          <span>
                            {quality === "auto"
                              ? "خودکار"
                              : formatQuality(quality)}
                          </span>
                          {currentQuality === quality && (
                            <Icon
                              name="check"
                              size={18}
                              className="text-white"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
});

SettingsMenu.displayName = "SettingsMenu";
